import React, { useState, useEffect } from "react";
import { useMutation, useManualQuery } from "graphql-hooks";
import {
  Avatar,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AlertPopup from "./components/AlertPopup";
import FloatingMenu from "./components/FloatingMenu";
import EmptyState from "./components/EmptyState";
import { styled } from "@mui/material/styles";

import { FOLLOWINGS_BY_USER_UUID, CREATE_FOLLOW_MUTATION, DELETE_FOLLOW_MUTATION } from "./graphQL/follow_qry";

// Styled Components
const PageContainer = styled(Box)({
  backgroundColor: "#f5f5f5",
});

const MainContainer = styled(Box)(({ theme }) => ({
  margin: "0 auto",
  padding: "24px 100px",
  [theme.breakpoints.down(1360)]: {
    padding: "24px 16px",
  },
}));

const MainContent = styled(Box)({
  flex: 1,
  background: "white",
  borderRadius: "8px",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  minHeight: "500px",
});

const sortList = ["Ascending", "Descending"];

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

const MyFollowings = () => {
  const [followings, setFollowings] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("Ascending");
  const [page, setPage] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [unfollowPopup, setUnfollowPopup] = useState({
    open: false,
    followingId: null,
    followingName: "",
  });

  const [getFollowingData] = useManualQuery(
    FOLLOWINGS_BY_USER_UUID, {
    fetchPolicy: "network-only",
  });

  const [createFollow] = useMutation(CREATE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unFollow] = useMutation(DELETE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  let userUuid = localStorage.getItem("userUuid") ? localStorage.getItem("userUuid") : null;

  // Set rows per page based on screen size
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth <= 833;
      setRowsPerPage(isMobile ? 5 : 10);
      setPage(0); // Reset to first page when changing rows per page
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const setFollowingList = async () => {    
    if (!userUuid) return;
    const {
      loading: loadingFollowingListData,
      error: errorFollowingListData,
      data: followingListData,
    } = await getFollowingData({
      variables: {
        userUuid: userUuid,
      },
    });
    if (!loadingFollowingListData && followingListData && followingListData.followingsByUseruuid) {              
      setFollowings(
        followingListData.followingsByUseruuid.map(follow => ({
          id: follow.followId,
          uuid: follow.uuid,
          name: follow.userId.firstName + " " + follow.userId.lastName,
          username: "@" + follow.userId.username,
          userUuid: follow.userId.userUuid,
          userId: follow.userId.id,
          followerId: follow.followerId.id,
          followerUuid: follow.followerId.userUuid,
          avatar: follow.userId.avatarFileName,
          following: true           
        }))
      );
      setIsLoaded(true);
    } else {
      console.log("errorCurrStoryData: ", errorFollowingListData);
    };
  };

  useEffect(() => {
    (async () => setFollowingList())();
  }, [userUuid]);

  const handleFollowClick = async(id) => {
    const following = followings.find((f) => f.id === id);
    if (following.following) {
      // Show unfollow confirmation popup
      setUnfollowPopup({
        open: true,
        followingId: id,
        followingName: following.name,
      });
    } else {
      //save follow to database
      const { data, error } =  await createFollow({
        variables: {
          userUuid: following.userUuid,          
        },
      });
            
      if (error) {
        console.log("Error", error, data);             
      } else {
        console.log("Success",data);
        // Direct follow action
        toggleFollow(id);
      };      
    };
  };

  const toggleFollow = (id) => {    
    setFollowings((prev) =>
      prev.map((f) => (f.id === id ? { ...f, following: !f.following } : f))
    );         
  };

  const handleConfirmUnfollow = async() => {
    //save unfollow to database
    const following = followings.find((f) => f.id === unfollowPopup.followingId);    
    const { data, error } =  await unFollow({
      variables: {
        userUuid: following.userUuid,        
      },
    });

    if (error) {
      console.log("Error", error, data);      
    } else {
      console.log("Success",data);
      toggleFollow(unfollowPopup.followingId);
      setUnfollowPopup({ open: false, followingId: null, followingName: "" });
    }
  };

  const handleCancelUnfollow = () => {
    setUnfollowPopup({ open: false, followingId: null, followingName: "" });
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // Filter and sort followings
  const getFilteredAndSortedFollowings = () => {
    let filtered = followings.filter((f) =>
      f.name.toLowerCase().includes(search.toLowerCase())
    );

    // Sort by full name
    filtered.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      if (sortBy === "Ascending") {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });

    return filtered;
  };

  const filteredFollowings = getFilteredAndSortedFollowings();

  // Get current page data from filtered and sorted results
  const paginatedFollowings = filteredFollowings.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <PageContainer>
      <MainContainer>
        <MainContent>
          {/* Header */}
          <Box p={2}>
            <Typography variant="h6">Followings</Typography>
            <Typography variant="body2" color="text.secondary">
              People whose updates and activities you follow.
            </Typography>
          </Box>

          {/* Divider under header */}
          <Divider sx={{ mb: 2 }} />

          {!isLoaded ? (
            <Box p={4} textAlign="center">Loading...</Box>
          ) : followings.length === 0 ? (
            <EmptyState 
              type="following"
              showIcon={false}
              title="No Following Available"
              description="You are not following anyone yet."
            />
          ) : (
            <>
              {/* Search and Sort Section */}
              <Box pr={2} pl={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                    bgcolor: "#F5F5F5", // Gray box around
                    p: "16px",
                    borderRadius: 2,
                    "@media (max-width: 833px)": {
                      flexDirection: "column",
                      alignItems: "stretch",
                    },
                  }}
                >
                  {/* Search box */}
                  <TextField
                    fullWidth
                    placeholder="Search followings..."
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      bgcolor: "#fff",
                      flexGrow: 1,
                      borderRadius: 1,
                      "& .MuiOutlinedInput-root": {
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                          borderWidth: "1px",
                        },
                      },
                    }}
                  />

                  {/* Sort dropdown */}
                  <FormControl
                    size="small"
                    sx={{
                      minWidth: "200px",
                      "@media (max-width: 833px)": {
                        width: "100%",
                        mt: 1, // Add margin top on mobile
                      },
                      bgcolor: "#fff",
                      borderRadius: 1,
                    }}
                  >
                    <InputLabel
                      sx={{
                        color: "#707070",
                        "&.Mui-focused": { color: "#707070" },
                      }}
                    >
                      Sort by
                    </InputLabel>
                    <Select
                      label="Sort by"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      displayEmpty
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                          borderWidth: "1px",
                        },
                      }}
                    >
                      {sortList.map((srt) => (
                        <MenuItem key={srt} value={srt}>
                          {srt}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Divider under search section */}
              <Divider />

              {/* EMPTY STATE vs TABLE */}
              {filteredFollowings.length === 0 ? (
                <Box p={4} textAlign="center" color="gray">
                  No Matching Followings
                </Box>
              ) : (
                <>          
                  {/* Followings Table */}
                  <TableContainer
                    component={Paper}
                    sx={{
                      overflow: "hidden",
                    }}
                  >
                    <Table>
                      <TableBody>
                        {paginatedFollowings.map((f, index) => (
                          <TableRow
                            key={f.id}
                            sx={{
                              borderBottom: "1px solid #e0e0e0",
                              "&:hover": {
                                backgroundColor: "#f9f9f9",
                              },
                            }}
                          >
                            <TableCell sx={{ py: 1, px: 2, border: "none" }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar src={f.avatar} alt={f.name} />
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {f.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {f.username}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell
                              sx={{ py: 2, px: 2, textAlign: "right", border: "none" }}
                            >
                              <StyledTooltip
                                title={
                                  f.following
                                    ? "You are following this person"
                                    : "Follow this person"
                                }
                              >
                                <Button
                                  variant="contained"
                                  startIcon={
                                    f.following ? (
                                      <PersonOutlineOutlinedIcon />
                                    ) : (
                                      <PersonAddAlt1OutlinedIcon />
                                    )
                                  }
                                  onClick={() => handleFollowClick(f.id)}
                                  sx={{
                                    width: "97px",
                                    height: "32px",
                                    backgroundColor: f.following ? "#F3E5F5" : "#7B1FA2",
                                    color: f.following ? "#4A148C" : "white",
                                    fontSize: "12px",
                                    fontWeight: "500",
                                    textTransform: "none",
                                    borderRadius: "6px",
                                    border: f.following ? "1px solid #4A148C" : "none",
                                    boxShadow: "none",
                                    "&:hover": {
                                      backgroundColor: f.following ? "#EFDBE3" : "#5a1a73",
                                      boxShadow: "none",
                                    },
                                    "& .MuiButton-startIcon": {
                                      color: f.following ? "#4A148C" : "white",
                                    },
                                  }}
                                >
                                  {f.following ? "Following" : "Follow"}
                                </Button>
                              </StyledTooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 15, 20]}
                      component="div"
                      count={filteredFollowings.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        borderTop: "1px solid #e0e0e0",
                        "& .MuiTablePagination-toolbar": {
                          paddingLeft: 2,
                          paddingRight: 2,
                        },
                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                          fontSize: "14px",
                          color: "#666",
                        },
                        "& .MuiTablePagination-select": {
                          fontSize: "14px",
                        },
                      }}
                    />
                  </TableContainer>
                </>
              )}
            </>
          )}

          {/* Unfollow Confirmation Popup */}
          <AlertPopup
            open={unfollowPopup.open}
            title="Unfollow This Person"
            message={
              <>
                Are you sure you want to unfollow user{" "}
                <strong>{unfollowPopup.followingName}</strong>?
              </>
            }
            confirmText="YES, UNFOLLOW"
            cancelText="CANCEL"
            onConfirm={handleConfirmUnfollow}
            onCancel={handleCancelUnfollow}
            onClose={handleCancelUnfollow}
            showCheckCircle={true}
          />

          <FloatingMenu left={floatMenuLeftMargin} />
        </MainContent>
      </MainContainer>
    </PageContainer>
  );
};
export default MyFollowings;
